// Widget registry + context builder (Phase 3/4).
//
// Originally a `window.bentoReact` mount bridge (Phase 3 coexistence): the legacy
// `refreshModuleBody` asked `bentoReact.has(title)` and, if so, called
// `bentoReact.render(...)` to hand a module body a `createRoot`. Phase 4's shell
// cutover (PR 9/10) made `<PlacedModule>` compose `<ModuleBody>` directly instead
// — no bridge, no per-body root — so the mounting wrapper (`bentoReact`,
// `createRoot`, the roots `WeakMap`) was removed in PR 11. What's left is the
// title→component `REGISTRY` and `buildWidgetContext`, both pure and imported
// directly by `<ModuleBody>` (`components/ModuleBody.tsx`).

import { createElement, type ComponentType } from "react";
import type { ModuleControls as ModuleControlsState } from "./store/module.js";
import { teamInFixture } from "./lib/fixtureTeams.js";
import CorrectScore from "./components/widgets/CorrectScore.js";
import LeagueTable from "./components/widgets/LeagueTable.js";
import FormTable from "./components/widgets/FormTable.js";
import XgTable from "./components/widgets/XgTable.js";
import InjurySuspension from "./components/widgets/InjurySuspension.js";
import DoubtfulPlayers from "./components/widgets/DoubtfulPlayers.js";
import NewsSentiment from "./components/widgets/NewsSentiment.js";
import PenaltyTakers from "./components/widgets/PenaltyTakers.js";
import ValueIndicator from "./components/widgets/ValueIndicator.js";
import MarginCalculator from "./components/widgets/MarginCalculator.js";
import FormBadges from "./components/widgets/FormBadges.js";
import TopScorers from "./components/widgets/TopScorers.js";
import Assists from "./components/widgets/Assists.js";
import WidgetComingSoon from "./components/widgets/WidgetComingSoon.js";
import ScoredConceded from "./components/widgets/ScoredConceded.js";
import PenaltyRecord from "./components/widgets/PenaltyRecord.js";
import OverUnder from "./components/widgets/OverUnder.js";
import FirstGoal from "./components/widgets/FirstGoal.js";
import SetPiece from "./components/widgets/SetPiece.js";
import MatchRate from "./components/widgets/MatchRate.js";
import GoalsByTimeBand from "./components/widgets/GoalsByTimeBand.js";
import HtFt from "./components/widgets/HtFt.js";
import RefereeProfile from "./components/widgets/RefereeProfile.js";
import RefereeXClub from "./components/widgets/RefereeXClub.js";
import RefereeTrend from "./components/widgets/RefereeTrend.js";
import MatchStates from "./components/widgets/MatchStates.js";
import Trajectory from "./components/widgets/Trajectory.js";
import XgTimeline from "./components/widgets/XgTimeline.js";
import HalfGoals from "./components/widgets/HalfGoals.js";
import HeadToHead from "./components/widgets/HeadToHead.js";
import HeadToHeadTrends from "./components/widgets/HeadToHeadTrends.js";
import MatchGoals from "./components/widgets/MatchGoals.js";
import MatchCards from "./components/widgets/MatchCards.js";
import Winner1x2 from "./components/widgets/Winner1x2.js";
import PlayerGoals from "./components/widgets/PlayerGoals.js";
import PlayerCards from "./components/widgets/PlayerCards.js";
import GoalsMarketMovement from "./components/widgets/GoalsMarketMovement.js";
import CardsMarketMovement from "./components/widgets/CardsMarketMovement.js";
import Winner1x2Movement from "./components/widgets/Winner1x2Movement.js";
import PlayerMovement from "./components/widgets/PlayerMovement.js";

/** Context the legacy shell passes down to a converted widget. Grows as more
 *  renderers (which read controls) are ported — each field is read from the
 *  module's shell toggles in refreshModuleBody. */
export interface WidgetContext {
  fixture: string;
  /** Season toggle value (League Table); null when absent. */
  season?: string | null;
  /** Venue toggle value (Form Table): "All" | "Home" | "Away"; null when absent. */
  ha?: string | null;
  /** Timeline toggle value (Form / xG tables): "5" | "10" | "Season"; null when absent. */
  timeline?: string | null;
  /** Rate toggle value (xG Table): "Per Game" | "Per Season"; null when absent. */
  rate?: string | null;
  /** Team toggle value (Injuries & Suspension): full team name; null when absent. */
  team?: string | null;
  /** Split toggle value (First Goal): "SF" | "CF"; null when absent. */
  split?: string | null;
  /** View toggle value (First Goal): "Outcome" | "Frequency"; null when absent. */
  view?: string | null;
  /** BTTS view-mode (from mod.dataset.bttsView): "donut" | "blocks"; null when absent. */
  bttsView?: string | null;
  /** Clean Sheets view-mode (from mod.dataset.csView): "donut" | "blocks"; null when absent. */
  csView?: string | null;
  /** HT/FT view-mode (from mod.dataset.htftView): "flow" | "bars"; null when absent. */
  htftView?: string | null;
  /** Referee toggle value (referee modules): referee name; null when absent. */
  ref?: string | null;
  /** Referee-season toggle (Referee x Club): "This Season" | "Last Season" | "All Time". */
  refSeason?: string | null;
  /** 1st/2nd Half Goals view-mode (from mod.dataset.hgView): "bands" | "bars". */
  hgView?: string | null;
  /** Seasons toggle value (Head to Head Trends): "3" | "5" | "All"; null when absent. */
  seasons?: string | null;
  /** Bookmaker toggle value (market family): bookmaker name; null when absent. */
  bookmaker?: string | null;
  /** Market-scope toggle value (market family): "Match" | team abbr; null when absent. */
  scope?: string | null;
  /** Market-type value (Player markets, from mod.dataset.marketType): "Anytime" | "First" | "2+". */
  marketType?: string | null;
  /** Market-line toggle value (movement charts): line/outcome key; null when absent. */
  marketLine?: string | null;
  /** Player toggle value (Player movements): selected player; empty/null on first mount. */
  player?: string | null;
}

/** Build a widget's `WidgetContext` from the store's control values (Phase 4).
 *  Mirrors the store branch of the legacy `refreshModuleBody` exactly: a module
 *  has at most one view-mode toggle button, so the single store `viewMode` fills
 *  whichever `*View` field the widget reads (the rest are harmless — that widget
 *  isn't rendering). Used by `<ModuleBody>` (the React shell) so the body no
 *  longer flows through the `window.bentoReact` bridge on desktop. */
export function buildWidgetContext(
  fixture: string,
  c: Partial<ModuleControlsState> | undefined | null
): WidgetContext {
  const v = c ?? {};
  return {
    fixture,
    season: v.season ?? null,
    ha: v.ha ?? null,
    timeline: v.timeline ?? null,
    rate: v.rate ?? null,
    // A team saved on another match (e.g. a board reopened on a new fixture) is
    // dropped, so the widget shows the home team — matching the toggle.
    team: teamInFixture(v.team, fixture),
    split: v.split ?? null,
    view: v.view ?? null,
    seasons: v.seasons ?? null,
    bttsView: v.viewMode ?? null,
    csView: v.viewMode ?? null,
    htftView: v.viewMode ?? null,
    hgView: v.viewMode ?? null,
    ref: v.ref ?? null,
    refSeason: v.refSeason ?? null,
    bookmaker: v.bookmaker ?? null,
    scope: v.scope ?? null,
    marketType: v.marketType ?? null,
    marketLine: v.marketLine ?? null,
    player: v.player ?? null,
  };
}

// Recent Form and Home/Away Form share one component (FormBadges) — thin
// wrappers fix the isHomeAway flag and forward the control values.
const RecentForm = (ctx: WidgetContext) =>
  createElement(FormBadges, {
    isHomeAway: false,
    fixture: ctx.fixture,
    timeline: ctx.timeline,
    ha: ctx.ha,
  });
const HomeAwayForm = (ctx: WidgetContext) =>
  createElement(FormBadges, {
    isHomeAway: true,
    fixture: ctx.fixture,
    timeline: ctx.timeline,
    ha: ctx.ha,
  });

// The player/discipline "club" family (top-scorers-club, assists-club,
// shots-on-target-club, player-discipline, team-discipline) still only has dummy
// data for the original 10 teams, so it renders a deliberate "coming soon"
// placeholder on EVERY fixture (Option A) instead of a bare "No data" once the
// full selector is on. Wired here at the registry — the real components stay
// intact and dormant, ready to re-enable when real per-player data lands.
const comingSoon =
  (label: string): ComponentType<WidgetContext> =>
  () =>
    createElement(WidgetComingSoon, { label });

// Top Scorers / Assists League still show real (league-wide) data — only their
// Club siblings are deferred (see comingSoon above). Both share one component;
// this wrapper fixes the league variant and forwards its controls.
const TopScorersLeague = (ctx: WidgetContext) =>
  createElement(TopScorers, {
    variant: "league",
    fixture: ctx.fixture,
    ha: ctx.ha,
    timeline: ctx.timeline,
  });
const AssistsLeague = (ctx: WidgetContext) =>
  createElement(Assists, {
    variant: "league",
    fixture: ctx.fixture,
    ha: ctx.ha,
    timeline: ctx.timeline,
  });

// First Goal (split + timeline + view) and Set Piece Goals (team + timeline)
// are half-donut widgets — thin wrappers forward just the controls each reads.
const FirstGoalWidget = (ctx: WidgetContext) =>
  createElement(FirstGoal, {
    fixture: ctx.fixture,
    split: ctx.split,
    timeline: ctx.timeline,
    view: ctx.view,
  });
const SetPieceWidget = (ctx: WidgetContext) =>
  createElement(SetPiece, {
    fixture: ctx.fixture,
    team: ctx.team,
    timeline: ctx.timeline,
  });

// BTTS + Clean Sheets share one component (MatchRate) — thin wrappers fix the
// kind and map the widget's view-mode dataset value to `view`.
const BttsWidget = (ctx: WidgetContext) =>
  createElement(MatchRate, {
    kind: "btts",
    fixture: ctx.fixture,
    ha: ctx.ha,
    timeline: ctx.timeline,
    view: ctx.bttsView,
  });
const CleanSheetsWidget = (ctx: WidgetContext) =>
  createElement(MatchRate, {
    kind: "cleanSheet",
    fixture: ctx.fixture,
    ha: ctx.ha,
    timeline: ctx.timeline,
    view: ctx.csView,
  });

// Goals by Time Band (team + timeline) + HT/FT (team + timeline + view-mode).
const GoalsByTimeBandWidget = (ctx: WidgetContext) =>
  createElement(GoalsByTimeBand, {
    fixture: ctx.fixture,
    team: ctx.team,
    timeline: ctx.timeline,
  });
const HtFtWidget = (ctx: WidgetContext) =>
  createElement(HtFt, {
    fixture: ctx.fixture,
    team: ctx.team,
    timeline: ctx.timeline,
    view: ctx.htftView,
  });

// Referee family — all read the .ref-toggle; Referee x Club adds team + season.
const RefereeProfileWidget = (ctx: WidgetContext) =>
  createElement(RefereeProfile, {
    fixture: ctx.fixture,
    ref: ctx.ref,
    rate: ctx.rate,
  });
const RefereeXClubWidget = (ctx: WidgetContext) =>
  createElement(RefereeXClub, {
    fixture: ctx.fixture,
    ref: ctx.ref,
    team: ctx.team,
    refSeason: ctx.refSeason,
  });
const RefereeTrendWidget = (ctx: WidgetContext) =>
  createElement(RefereeTrend, { fixture: ctx.fixture, ref: ctx.ref });

// D3 chart trio. Match States (team + timeline + ha) — radial clock, clear+redraw.
// Performance Trajectory (team + timeline + view Rolling/Actual) — animated lines.
const MatchStatesWidget = (ctx: WidgetContext) =>
  createElement(MatchStates, {
    fixture: ctx.fixture,
    timeline: ctx.timeline,
    team: ctx.team,
    ha: ctx.ha,
  });
const TrajectoryWidget = (ctx: WidgetContext) =>
  createElement(Trajectory, {
    fixture: ctx.fixture,
    team: ctx.team,
    timeline: ctx.timeline,
    view: ctx.view,
  });
// xG Timeline (team + timeline + view Rolling/Actual) — area/line, animated.
const XgTimelineWidget = (ctx: WidgetContext) =>
  createElement(XgTimeline, {
    fixture: ctx.fixture,
    timeline: ctx.timeline,
    team: ctx.team,
    view: ctx.view,
  });

// 1st/2nd Half Goals — team + timeline + view-mode (bands default / bars).
const HalfGoalsWidget = (ctx: WidgetContext) =>
  createElement(HalfGoals, {
    fixture: ctx.fixture,
    team: ctx.team,
    timeline: ctx.timeline,
    view: ctx.hgView,
  });

// Head to Head (ha-toggle) + Head to Head Trends (seasons-toggle) — match-domain
// widgets; thin wrappers forward just the controls each reads.
const HeadToHeadWidget = (ctx: WidgetContext) =>
  createElement(HeadToHead, { fixture: ctx.fixture, ha: ctx.ha });
const HeadToHeadTrendsWidget = (ctx: WidgetContext) =>
  createElement(HeadToHeadTrends, {
    fixture: ctx.fixture,
    seasons: ctx.seasons,
  });

// Match Goals + Match Cards — the market-table type; thin wrappers forward the
// bookmaker + scope controls. Cross-module highlight sync stays legacy (Phase 4).
const MatchGoalsWidget = (ctx: WidgetContext) =>
  createElement(MatchGoals, {
    fixture: ctx.fixture,
    bookmaker: ctx.bookmaker,
    scope: ctx.scope,
  });
const MatchCardsWidget = (ctx: WidgetContext) =>
  createElement(MatchCards, {
    fixture: ctx.fixture,
    bookmaker: ctx.bookmaker,
    scope: ctx.scope,
  });

// Winner (1x2) reuses <MarketTable> (bookmaker only). Player Goals/Cards use the
// sibling <PlayerMarketTable> (bookmaker + scope + market type).
const Winner1x2Widget = (ctx: WidgetContext) =>
  createElement(Winner1x2, { fixture: ctx.fixture, bookmaker: ctx.bookmaker });
const PlayerGoalsWidget = (ctx: WidgetContext) =>
  createElement(PlayerGoals, {
    fixture: ctx.fixture,
    bookmaker: ctx.bookmaker,
    scope: ctx.scope,
    marketType: ctx.marketType,
  });
const PlayerCardsWidget = (ctx: WidgetContext) =>
  createElement(PlayerCards, {
    fixture: ctx.fixture,
    bookmaker: ctx.bookmaker,
    scope: ctx.scope,
    marketType: ctx.marketType,
  });

// Movement charts (D3 line charts) — bookmaker + market line; Goals/Cards add
// scope. Cross-module highlight sync stays legacy (Phase 4).
const GoalsMarketMovementWidget = (ctx: WidgetContext) =>
  createElement(GoalsMarketMovement, {
    fixture: ctx.fixture,
    bookmaker: ctx.bookmaker,
    scope: ctx.scope,
    marketLine: ctx.marketLine,
  });
const CardsMarketMovementWidget = (ctx: WidgetContext) =>
  createElement(CardsMarketMovement, {
    fixture: ctx.fixture,
    bookmaker: ctx.bookmaker,
    scope: ctx.scope,
    marketLine: ctx.marketLine,
  });
const Winner1x2MovementWidget = (ctx: WidgetContext) =>
  createElement(Winner1x2Movement, {
    fixture: ctx.fixture,
    bookmaker: ctx.bookmaker,
    marketLine: ctx.marketLine,
  });

// Player movements — bookmaker + scope + market type; the component owns the
// shell .player-toggle (`player` is empty on first mount, see PlayerMovement).
const PlayerGoalsMovementWidget = (ctx: WidgetContext) =>
  createElement(PlayerMovement, {
    kind: "goals",
    fixture: ctx.fixture,
    bookmaker: ctx.bookmaker,
    scope: ctx.scope,
    marketType: ctx.marketType,
    player: ctx.player,
  });
const PlayerCardsMovementWidget = (ctx: WidgetContext) =>
  createElement(PlayerMovement, {
    kind: "cards",
    fixture: ctx.fixture,
    bookmaker: ctx.bookmaker,
    scope: ctx.scope,
    marketType: ctx.marketType,
    player: ctx.player,
  });

/** Module title → React component, read directly by `<ModuleBody>` (every
 *  title is registered — Phase 3 converted all ~46 renderers). */
export const REGISTRY: Record<string, ComponentType<WidgetContext>> = {
  "Correct Score Matrix": CorrectScore,
  "League Table": LeagueTable,
  "Form Table": FormTable,
  "xG Table": XgTable,
  "Injuries & Suspension": InjurySuspension,
  "Doubtful Players": DoubtfulPlayers,
  "News & Sentiment": NewsSentiment,
  "Penalty Takers": PenaltyTakers,
  "Player Discipline": comingSoon("Player discipline"),
  "Value Indicator": ValueIndicator,
  "Margin Calculator": MarginCalculator,
  "Recent Form": RecentForm,
  "Home/Away Form": HomeAwayForm,
  "Top Scorers — League": TopScorersLeague,
  "Top Scorers — Club": comingSoon("Top scorers"),
  "Assists — League": AssistsLeague,
  "Assists — Club": comingSoon("Assists"),
  "Shots on Target — Club": comingSoon("Shots on target"),
  "Team Discipline": comingSoon("Team discipline"),
  "Scored & Conceded": ScoredConceded,
  "Penalty Record": PenaltyRecord,
  "Over / Under Rates": OverUnder,
  "First Goal": FirstGoalWidget,
  "Set Piece Goals": SetPieceWidget,
  "BTTS": BttsWidget,
  "Clean Sheets": CleanSheetsWidget,
  "Goals by Time Band": GoalsByTimeBandWidget,
  "HT/FT": HtFtWidget,
  "Referee Profile": RefereeProfileWidget,
  "Referee x Club": RefereeXClubWidget,
  "Referee Trend": RefereeTrendWidget,
  "Match States": MatchStatesWidget,
  "Performance Trajectory": TrajectoryWidget,
  "xG Timeline": XgTimelineWidget,
  "1st/2nd Half Goals": HalfGoalsWidget,
  "Head to Head": HeadToHeadWidget,
  "Head to Head Trends": HeadToHeadTrendsWidget,
  "Match Goals": MatchGoalsWidget,
  "Match Cards": MatchCardsWidget,
  "Winner (1x2)": Winner1x2Widget,
  "Player Goals": PlayerGoalsWidget,
  "Player Cards": PlayerCardsWidget,
  "Match Goals - Movement": GoalsMarketMovementWidget,
  "Match Cards - Movement": CardsMarketMovementWidget,
  "Winner (1x2) - Movement": Winner1x2MovementWidget,
  "Player Goals - Movement": PlayerGoalsMovementWidget,
  "Player Cards - Movement": PlayerCardsMovementWidget,
};
